import type { CaseStudy } from '../../types/content'

export const meridianArchitects: CaseStudy = {
  slug: 'meridian-architects',
  summary:
    'A self-initiated concept. We invented a six-person architecture practice and built the identity and site we think that kind of studio deserves — one that argues a position instead of showing a portfolio.',
  overview: {
    industry: 'Concept · Architecture practice',
    location: 'Concept · India',
    timeline: 'Self-initiated concept, 2026',
    services: ['Brand strategy', 'Identity design', 'Art direction', 'Website design'],
  },
  problem: {
    heading: 'The problem',
    paragraphs: [
      'This one is ours. No client commissioned it, nobody paid for it, and the practice does not exist. We publish it because it shows how we think about identity when there is no legacy to protect, and because the problem it solves is one we meet constantly.',
      'Small architecture practices are hard to tell apart. Almost every studio site in the bracket follows the same pattern: a full-bleed photograph of a completed house, a grid of projects, a short paragraph about light and materiality, and a contact page. The work may be excellent. The presentation says nothing about how the practice thinks, who it argues with, or what it refuses to do.',
      'The commercial cost is real. A practice that presents as a portfolio competes on drawings and gets paid for drawings. A practice that presents a position competes on judgement, and judgement is the only part of the service that carries a margin. We wanted to design the second kind.',
    ],
  },
  research: {
    heading: 'Assumptions and discovery',
    paragraphs: [
      'This is concept work, so nothing below is a research finding. These are the assumptions the design was built against, drawn from publicly visible material rather than from a study we commissioned.',
      'We reviewed a sample of practice websites in India and abroad, noting what each one leads with. Almost all of them open on a hero image of a finished building; a few lead with awards. A written position is rare enough that it is memorable an hour later, which is the whole opportunity.',
      'We worked from publicly available accounts of how architects describe winning work — written interviews, practice journals, conference talks. The consistent picture is that work arrives through repeat developers, planning officers, other architects, and people who have read something the practice wrote. Nobody credits a project gallery.',
      'That gave us the strategic wedge we designed against. We assume the gallery is not what wins the work; it is what confirms the work once someone is already interested. So the site could lead with something else entirely.',
      'We also collected the vocabulary. Reading practice manifestos back to back, the same nine words recur in almost all of them — light, materiality, context, dialogue, craft. Any identity we built would have to earn its language rather than borrow that list.',
    ],
  },
  artifacts: [
    {
      kind: 'audit',
      title: 'Practice website review',
      caption: 'A sample of studios noted by what the homepage leads with',
    },
    {
      kind: 'inventory',
      title: 'Language inventory',
      caption: 'The nine words every practice manifesto already uses',
    },
    {
      kind: 'sitemap',
      title: 'Position-led structure',
      caption: 'Written position first, project archive second',
    },
  ],
  strategy: {
    heading: 'Strategy and decisions',
    paragraphs: [
      'We gave Meridian a position we could defend: the practice works on buildings that will be altered by their occupants, and designs for the second and third alteration rather than the photograph on completion day. Everything in the identity follows from that.',
      'It changes the site structure immediately. The homepage opens with a written statement, not an image. Projects are presented as records with dates, revisions and what changed after handover — an archive rather than a gallery. The team page names what each person argues for.',
      'It also changes the identity system. A practice interested in alteration should not have a logo that only works one way, so the monogram is drawn as a modular mark on a four-unit grid with three sanctioned configurations, each correct in a different format.',
    ],
  },
  decisions: [
    {
      decision: 'Opened the homepage with text, not a building',
      reasoning:
        'Almost every practice site in the bracket leads with a hero photograph. Leading with a position is both more differentiated and closer to how we assume the work actually arrives.',
    },
    {
      decision: 'Presented projects as records, not a gallery',
      reasoning:
        'Dates, revisions and post-handover changes demonstrate judgement over time. A gallery only demonstrates that a photographer visited once.',
    },
    {
      decision: 'Drew the monogram as a modular mark',
      reasoning:
        'A practice whose whole argument is alteration cannot have an identity that breaks when it is reconfigured. Three sanctioned states, one grid.',
    },
    {
      decision: 'Banned the nine borrowed words from the copy',
      reasoning:
        'Light, materiality and dialogue are not wrong — they are unowned. Removing them forced the writing to be specific.',
    },
  ],
  design: {
    heading: 'Design direction',
    paragraphs: [
      'The identity sits on paper before it sits on screen. A warm stone ground, a single graphite ink, and two muted accents — sage and clay — drawn from the material palette a practice like this would actually specify. The printed system came first: cards, a folded brochure, an embossed monogram card, a swatch strip. The website inherits that surface rather than inventing a digital one.',
      'Typography does the arguing. A high-contrast serif sets the written position at a size that expects to be read, with the measure capped tight so a paragraph looks like an essay rather than a caption. All metadata — dates, materials, revision numbers, planning references — sits in a neutral sans at a single small size, which lets the archive read as a record.',
      'The component set is drawn from architectural drawing conventions: hairline rules instead of cards, a four-unit grid visible in the margins, a project record with a revision table, and a specification list. There are no shadows and no filled panels anywhere in the system. Structure is expressed by alignment and rule weight, which is how the discipline itself expresses it.',
    ],
  },
  typefaces: [
    { name: 'Instrument Serif', role: 'Written position and project titles' },
    { name: 'Inter Tight', role: 'Metadata, specifications and navigation' },
  ],
  palette: [
    { name: 'Stone', hex: '#F2EFE9' },
    { name: 'Graphite', hex: '#1B1B19' },
    { name: 'Sage', hex: '#7C8C77' },
    { name: 'Clay', hex: '#B07A5F' },
  ],
  components: [
    'Modular monogram, three states',
    'Project record with revision table',
    'Specification list',
    'Four-unit margin grid',
    'Position statement block',
  ],
  mockupCaption: 'Identity system and website concept, desktop and mobile',
  results: {
    isProjected: true,
    intro:
      'This is self-initiated concept work. No client engaged us and nothing here has been measured in market. The figures below are the outcomes we would set as targets if a practice ran this system, stated as expectations rather than results.',
    metrics: [
      { value: 'Fee basis', label: 'Judgement rather than drawing volume' },
      { value: 'Position first', label: 'Homepage leads with an argument, not an image' },
      { value: '3 states', label: 'Monogram configurations, one grid' },
      { value: 'Own language', label: 'Copy written without the nine borrowed words' },
    ],
    note:
      'We would judge this successful if a practice using it stopped being asked to compete on drawing packages. That is a claim we cannot make yet, so we are not making it.',
  },
  quote: {
    text:
      'We build concept work like this to put an argument on the table properly. It is honest about being a concept, and it shows the reasoning without anything redacted.',
    name: 'ShreyasStudio',
    role: 'Self-initiated concept, 2026',
  },
}
